import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const LanguageSelector = () => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isChanging, setIsChanging] = useState(false);
  const dropdownRef = useRef(null);

  const languages = [
    { code: 'en', name: 'English', flag: '', short: 'EN' },
    { code: 'ko', name: '', flag: '', short: 'KO' },
  ];

  const currentLang = languages.find(lang => lang.code === i18n.language) || languages[1];

  const changeLanguage = async (langCode) => {
    if (langCode === i18n.language) {
      setIsOpen(false);
      return;
    }
    setIsChanging(true);
    try {
      await i18n.changeLanguage(langCode);
      localStorage.setItem('i18nextLng', langCode);
    } finally {
      setIsChanging(false);
      setIsOpen(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        className={`flex items-center gap-1 bg-transparent border-none py-1.5 px-2 cursor-pointer rounded-lg transition-colors duration-200 ${isChanging ? 'opacity-60 pointer-events-none' : ''} hover:bg-gray-100 active:bg-gray-200`}
        onClick={() => !isChanging && setIsOpen(!isOpen)}
        aria-label="언어 선택"
        disabled={isChanging}
        style={{ WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' }}
      >
        <span className={`text-sm font-semibold text-gray-900 tracking-wide ${isChanging ? 'animate-pulse' : ''}`}>{currentLang.short}</span>
        <svg className={`transition-transform duration-200 text-gray-400 ${isOpen ? 'rotate-180' : ''}`} width="10" height="6" viewBox="0 0 10 6">
          <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" fill="none" />
        </svg>
      </button>

      {isOpen && (
        <div
          className="absolute top-full right-0 mt-2 bg-white/[0.98] backdrop-blur-xl backdrop-saturate-[180%] rounded-xl shadow-lg overflow-hidden min-w-[140px] z-[1010] animate-[dropdownFadeIn_0.2s_ease] max-md:right-[-0.5rem]"
          style={{ boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(0, 0, 0, 0.05)' }}
        >
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => changeLanguage(lang.code)}
              className={`flex items-center gap-2.5 w-full py-3 px-3.5 bg-transparent border-none cursor-pointer transition-colors duration-150 text-left ${i18n.language === lang.code ? 'bg-blue-50' : ''} hover:bg-gray-100 active:bg-gray-200`}
              disabled={isChanging}
              style={{ WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' }}
            >
              <span className="text-xs font-semibold text-blue-500 bg-blue-50 py-0.5 px-1.5 rounded min-w-[28px] text-center flex-shrink-0">{lang.short}</span>
              <span className="flex-1 text-sm text-gray-900">{lang.name}</span>
              {i18n.language === lang.code && <span className="text-blue-500 font-semibold">✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
